import { Button, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { H2 } from "../headlines/Headlines";
import { LI, OL, UL } from "../lists/Lists";
import Paragraph from "../paragraph/Paragraph";
import ScrollIndicator from "../scrollindicator/ScrollIndicator";
import SEO, { LOCAL_BUSINESS_ID, SITE_URL } from "../seo/SEO";
import "./Leistungsseite.css";
import HintergrundOsteopathie from "./hintergrund-leistungen.webp";
import HintergrundBiotensegralesTraining from "./hintegrund-biotensegralestraining.webp";
import HintergrundBlutabnahme from "./hintergrund-blutabnahme.webp";
import HintergrundFutterberatung from "./hay.webp";
import HintergrundHofmag from "./hofmag0.webp";
import Osteo1 from "./osteo1.webp";
import Osteo2 from "./osteo2.webp";
import Osteo3 from "./osteo3.webp";
import Tense1 from "./tense1.webp";
import Tense2 from "./tense2.webp";
import Tense3 from "./tense3.webp";
import Aku1 from "./aku1.webp";
import Aku2 from "./aku2.webp";
import Aku3 from "./aku3.webp";
import Aku4 from "./aku4.webp";
import Hofmag1 from "./hofmag1.webp";
import Hofmag2 from "./hofmag2.webp";
import Hofmag3 from "./hofmag3.webp";
import {
  Headline,
  TextMitBildLinks,
  TextMitBildRechts,
} from "../textmitbild/TextMitBild";

type ServiceSchema = {
  "@context": "https://schema.org";
  "@type": "Service";
  name: string;
  description: string;
  url: string;
  provider: { "@id": string };
  areaServed: ReadonlyArray<{
    "@type": "City" | "AdministrativeArea";
    name: string;
  }>;
  offers?: {
    "@type": "Offer";
    price: number;
    priceCurrency: "EUR";
    url: string;
  };
};

function Leistungsseite(props: {
  title: string;
  description: string;
  slug: string;
  /**
   * Lowest representative price in EUR. Used for schema.org `offers` so
   * Google can surface a price hint in the SERP. Omit when the page has
   * no clear single price (e.g. tiered packages).
   */
  priceFrom?: number;
  backgroundImage: "*.jpg";
  children: React.ReactNode[];
}) {
  const navigate = useNavigate();
  const serviceUrl = `${SITE_URL}/leistungen/${props.slug}`;
  // Note: no `availability` on the Offer — that field is for physical
  // inventory ("in stock"), not professional services. Schema.org
  // accepts the Offer without it.
  const offers: ServiceSchema["offers"] =
    props.priceFrom !== undefined
      ? {
          "@type": "Offer",
          price: props.priceFrom,
          priceCurrency: "EUR",
          url: serviceUrl,
        }
      : undefined;
  const serviceSchema: ServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: props.title,
    description: props.description,
    url: serviceUrl,
    provider: { "@id": LOCAL_BUSINESS_ID },
    areaServed: [
      { "@type": "City", name: "Hamburg" },
      { "@type": "AdministrativeArea", name: "Kreis Herzogtum Lauenburg" },
      { "@type": "AdministrativeArea", name: "Kreis Stormarn" },
    ],
    offers,
  };
  return (
    <div className="leistungsseite">
      <SEO
        title={props.title}
        description={props.description}
        breadcrumbs={[
          { name: "Start", path: "/" },
          { name: "Leistungen", path: "/leistungen" },
          { name: props.title, path: `/leistungen/${props.slug}` },
        ]}
        jsonLd={serviceSchema}
      />
      <div
        className="title-wrapper"
        style={{ backgroundImage: `url(${props.backgroundImage})` }}
      >
        <div className="title-shadow">
          <Typography variant="h1" align="center">
            {props.title}
          </Typography>
        </div>
        <ScrollIndicator />
      </div>
      <div className="content">{props.children}</div>
      <div className="button-wrapper">
        <Button
          sx={{ textTransform: "none", fontSize: "20px" }}
          variant="contained"
          fullWidth
          onClick={() =>
            navigate(
              "/kontakt?betreff=Terminanfrage+" +
                props.title.replaceAll(" ", "+")
            )
          }
        >
          &#x1F4C5; Termin vereinbaren
        </Button>
      </div>
    </div>
  );
}

export function OsteopathieUndPhysiotherapie() {
  return (
    <Leistungsseite
      title="Osteopathie und Physiotherapie"
      description="Osteopathische und physiotherapeutische Behandlung für Pferde in Hamburg und Umgebung. Blockaden lösen, Beweglichkeit fördern und nachhaltige Gesundheit unterstützen."
      slug="osteopathie-und-physiotherapie"
      priceFrom={110}
      backgroundImage={HintergrundOsteopathie}
    >
      <Headline>Ganzheitliche Unterstützung für dein Pferd</Headline>
      <TextMitBildRechts bild={Osteo3} alt="Osteopathische Behandlung an einem Pferd">
        <Paragraph>
          Pferde sind Meister der Bewegung - doch Fehlbelastungen, Verspannungen
          oder Blockaden können den natürlichen Bewegungsfluss stören und zu
          Unwohlsein oder sogar Schmerzen führen. Mit einer Kombination aus
          Osteopathie und Physiotherapie unterstütze ich dein Pferd dabei,
          wieder ins Gleichgewicht zu kommen, sich freier zu bewegen und
          langfristig gesund zu bleiben.
        </Paragraph>
        <H2>Was ist Pferdeosteopathie?</H2>
        <Paragraph>
          Die Osteopathie betrachtet den Körper als eine Einheit, in der alles
          miteinander verbunden ist - Muskeln, Gelenke, Faszien, Organe und das
          Nervensystem. Ziel ist es, Blockaden zu lösen, die Beweglichkeit zu
          verbessern und die Selbstheilungskräfte des Körpers zu aktivieren.
          Durch sanfte Techniken werden Bewegungseinschränkungen erkannt und
          behoben, sodass dein Pferd wieder losgelassen und schmerzfrei laufen
          kann.
        </Paragraph>
        <H2>Einsatzgebiete der Osteopathie</H2>
        <UL>
          <LI>Bewegungseinschränkungen oder Taktfehler</LI>
          <LI>Steifheit oder Widersetzlichkeit beim Reiten</LI>
          <LI>Schwierigkeiten in Stellung und Biegung</LI>
          <LI>Leistungsabfall oder Unwilligkeit</LI>
          <LI>Probleme beim Angaloppieren oder im Übergang</LI>
          <LI>Auffälligkeiten in der Körperhaltung oder Muskelaufbau</LI>
        </UL>
      </TextMitBildRechts>
      <TextMitBildLinks bild={Osteo1} alt="Physiotherapeutische Behandlung der Pferdemuskulatur">
        <H2>Was ist Pferdephysiotherapie?</H2>
        <Paragraph>
          Während die Osteopathie den gesamten Körper als funktionelle Einheit
          betrachtet, konzentriert sich die Physiotherapie gezielt auf Muskeln,
          Faszien und Gelenke. Durch Massagen, Dehnübungen und
          Mobilisationstechniken werden Verspannungen gelöst, Muskelaufbau
          gefördert und die Beweglichkeit verbessert.
        </Paragraph>
        <H2>Einsatzgebiete der Physiotherapie</H2>
        <UL>
          <LI>Muskelverspannungen und verhärtete Faszien</LI>
          <LI>Unterstützung der Rehabilitation nach Verletzungen</LI>
          <LI>Förderung der Tragkraft und Rittigkeit</LI>
          <LI>Präventive Behandlung zur Gesunderhaltung</LI>
          <LI>Verbesserung der Bewegungskoordination</LI>
        </UL>
        <H2>Osteopathie & Physiotherapie - eine ideale Kombination</H2>
        <Paragraph>
          Durch die Verbindung von osteopathischen und physiotherapeutischen
          Techniken kann dein Pferd optimal unterstützt werden. Während die
          Osteopathie Blockaden im gesamten Körper löst und die Funktionalität
          der Strukturen wiederherstellt, hilft die Physiotherapie dabei, die
          Muskulatur gezielt aufzubauen und die Beweglichkeit nachhaltig zu
          verbessern.
        </Paragraph>
        <Paragraph>
          Mein Ziel ist es, nicht nur Symptome zu behandeln, sondern die Ursache
          zu finden und langfristig für mehr Wohlbefinden, Balance und
          Bewegungsfreude zu sorgen.
        </Paragraph>
      </TextMitBildLinks>
      <TextMitBildRechts bild={Osteo2} alt="Ablauf einer osteopathischen Untersuchung am Pferd">
        <H2>Ablauf einer Behandlung</H2>
        <OL>
          <LI>
            Anamnese & Beobachtung - Gespräch über die Vorgeschichte deines
            Pferdes und Analyse der Bewegung
          </LI>
          <LI>
            Untersuchung - Abtasten, Mobilitätsprüfung und Erkennen von
            Verspannungen oder Blockaden
          </LI>
          <LI>
            Behandlung - Individuelle Kombination aus osteopathischen und
            physiotherapeutischen Techniken
          </LI>
          <LI>
            Empfehlungen & Nachbetreuung - Übungen oder Anpassungen für den
            Alltag, um die Therapie zu unterstützen
          </LI>
        </OL>
        <Paragraph>Dauer: ca. 60-120 Minuten</Paragraph>
        <Paragraph>Kosten: 110-140 € zzgl. Anfahrt</Paragraph>
        <H2>Wann sollte eine Behandlung erfolgen?</H2>
        <UL>
          <LI>Zur Gesunderhaltung und Prävention</LI>
          <LI>Bei ersten Anzeichen von Bewegungseinschränkungen</LI>
          <LI>Als Unterstützung bei Muskel- und Faszienproblemen</LI>
          <LI>Nach Verletzungen oder Krankheiten zur Rehabilitation</LI>
          <LI>Zur Leistungssteigerung bei Sport- und Freizeitpferden</LI>
        </UL>
        <Paragraph>
          Jedes Pferd ist einzigartig - deshalb stimme ich jede Behandlung
          individuell auf die Bedürfnisse deines Pferdes ab. Mein Ziel ist es,
          mit viel Feingefühl und Fachwissen langfristige Lösungen statt
          kurzfristige Effekte zu schaffen.
        </Paragraph>
      </TextMitBildRechts>
    </Leistungsseite>
  );
}

export function BiotensegralesTraining() {
  return (
    <Leistungsseite
      title="Biotensegrales Training"
      description="Biotensegrales Training für Pferde in Hamburg: Balance, Tragkraft und gesunde Bewegungsmuster sanft und nachhaltig fördern – Bodenarbeit und unter dem Sattel."
      slug="biotensegrales-training"
      priceFrom={60}
      backgroundImage={HintergrundBiotensegralesTraining}
    >
      <Headline>
        Bewegung im Einklang - Stabilität, Balance und Tragfähigkeit
      </Headline>
      <TextMitBildRechts bild={Tense1} alt="Biotensegrales Training - Bewegungsarbeit mit dem Pferd">
        <Paragraph>
          Ein gesunder Bewegungsapparat ist die Grundlage für Wohlbefinden,
          Leistungsfähigkeit und Tragkraft. Das biotensegrale Training basiert
          auf den natürlichen Bewegungsmustern des Pferdes und hilft dabei,
          Balance, Beweglichkeit und Stabilität zu fördern - sanft, effektiv und
          nachhaltig.
        </Paragraph>
        <H2>Was ist biotensegrales Training?</H2>
        <Paragraph>
          Biotensegrales Training beruht auf dem Konzept der Biotensegrität, das
          den Körper als ein vernetztes System aus Spannung (Muskeln, Faszien,
          Sehnen) und Druckstrukturen (Knochen, Gelenke) betrachtet. Statt
          isolierte Muskeln zu trainieren, wird das Pferd als Ganzes gefördert -
          mit dem Ziel, gesunde Bewegungsmuster zu entwickeln, die den Körper
          stabilisieren und entlasten.
        </Paragraph>
        <Paragraph>
          Durch gezielte Übungen vom Boden oder unter dem Sattel wird das Pferd
          ermutigt, sein Gleichgewicht zu finden, die Tragfähigkeit zu
          verbessern und Verspannungen zu lösen, ohne in Kompensation oder
          Fehlhaltungen zu geraten.
        </Paragraph>
        <H2>Warum ist dieses Training so effektiv?</H2>
        <Paragraph>
          Viele Pferde haben Schwierigkeiten mit ihrer Körperbalance, sei es
          durch unpassende Belastung, frühere Verletzungen oder mangelnde
          Tragkraft. Klassisches Muskeltraining kann diese Probleme oft nicht
          vollständig lösen, da es nicht berücksichtigt, wie der gesamte Körper
          als Einheit funktioniert.
        </Paragraph>
      </TextMitBildRechts>
      <TextMitBildLinks bild={Tense2} alt="Biotensegrales Training - gezielte Übung zur Balance">
        <H2>Das biotensegrale Training setzt hier an:</H2>
        <UL>
          <LI>Fördert die Eigenwahrnehmung und Körperkoordination</LI>
          <LI>Unterstützt eine natürliche Aufrichtung ohne Zwang</LI>
          <LI>Hilft bei der Lösung von Verspannungen und Blockaden</LI>
          <LI>Stärkt die Tragfähigkeit und Muskulatur nachhaltig</LI>
          <LI>Erarbeitet gesunde Bewegungsabläufe ohne Kompensation</LI>
        </UL>
        <H2>Für welche Pferde ist biotensegrales Training geeignet?</H2>
        <Paragraph>
          Dieses Training ist für jedes Pferd sinnvoll - unabhängig von Alter,
          Ausbildungsstand oder Nutzung. Besonders profitieren:
        </Paragraph>
        <UL>
          <LI>
            Pferde mit Trageschwäche oder Schwierigkeiten in der Versammlung
          </LI>
          <LI>Pferde mit Bewegungseinschränkungen oder Verspannungen</LI>
          <LI>Pferde in der Rehabilitation nach Verletzungen</LI>
          <LI>
            Jungpferde, die eine gesunde Basis für das Reiten lernen sollen
          </LI>
          <LI>
            Sportpferde, um Leistungsfähigkeit und Tragkraft zu verbessern
          </LI>
          <LI>
            Freizeitpferde, um Fehlhaltungen und Überlastungen vorzubeugen
          </LI>
        </UL>
      </TextMitBildLinks>
      <TextMitBildRechts bild={Tense3} alt="Biotensegrales Training - Bodenarbeit mit dem Pferd">
        <H2>Wie läuft das Training ab?</H2>
        <Paragraph>
          Jede Einheit wird individuell an das Pferd angepasst und kann folgende
          Inhalte umfassen:
        </Paragraph>
        <OL>
          <LI>Analyse der Bewegung und Körperhaltung</LI>
          <LI>
            Gezielte Bodenarbeit zur Verbesserung der Balance und Tragfähigkeit
          </LI>
          <LI>Spezifische Übungen zur Mobilisierung und Kräftigung</LI>
          <LI>Korrekte Arbeit unter dem Sattel für eine gesunde Aufrichtung</LI>
          <LI>
            Individuelle Empfehlungen für den Alltag und das weitere Training
          </LI>
        </OL>
        <Paragraph>Dauer: ca. 45-60 Minuten</Paragraph>
        <Paragraph>Kosten: 60 € zzgl. Anfahrt</Paragraph>
        <Paragraph>
          Dieses Training hilft deinem Pferd, sich natürlich, kraftvoll und frei
          zu bewegen - ohne Druck, aber mit nachhaltiger Wirkung. Statt nur
          Symptome zu bekämpfen, wird die Ursache von Bewegungsproblemen erkannt
          und verbessert.
        </Paragraph>
      </TextMitBildRechts>
    </Leistungsseite>
  );
}

export function FutterberatungUndRationsberechnung() {
  return (
    <Leistungsseite
      title="Futterberatung und Rationsberechnung"
      description="Individuelle Futterberatung und Rationsberechnung für Pferde in Hamburg. Bedarfsgerechte Ernährung bei EMS, Cushing, Muskelaufbau oder Mangelerscheinungen."
      slug="futterberatung-und-rationsberechnung"
      priceFrom={110}
      backgroundImage={HintergrundFutterberatung}
    >
      <Headline>
        Die richtige Fütterung - die Basis für ein gesundes Pferd
      </Headline>
      <Paragraph>
        Eine ausgewogene und bedarfsgerechte Fütterung ist entscheidend für die
        Gesundheit, Leistungsfähigkeit und das Wohlbefinden deines Pferdes. Doch
        mit der Vielzahl an Futtermitteln und Ergänzungen kann es schwer sein,
        den Überblick zu behalten. Ich biete eine individuelle Futterberatung
        und Rationsberechnung an, die speziell auf die Bedürfnisse deines
        Pferdes abgestimmt ist.
      </Paragraph>
      <H2>Warum eine Futterberatung sinnvoll ist</H2>
      <Paragraph>
        Jedes Pferd hat individuelle Anforderungen an seine Ernährung - abhängig
        von Alter, Rasse, Haltung, Arbeitspensum und möglichen gesundheitlichen
        Herausforderungen. Eine unpassende Fütterung kann zu
        Mangelerscheinungen, Stoffwechselproblemen oder Verdauungsstörungen
        führen, während eine ausgewogene Ration die Basis für Kraft, Ausdauer
        und eine gesunde Muskulatur schafft.
      </Paragraph>
      <Paragraph>Eine Futterberatung kann helfen bei:</Paragraph>
      <UL>
        <LI>Unklaren Gewichtsveränderungen (zu dünn/zu dick)</LI>
        <LI>Muskelaufbau-Problemen trotz Training</LI>
        <LI>Mangelerscheinungen (z. B. Mineralstoffe, Vitamine)</LI>
        <LI>Stoffwechselerkrankungen wie EMS, Cushing oder PSSM</LI>
        <LI>Problemen mit Hufen, Fell oder Verdauung</LI>
        <LI>Trageschwäche und mangelnder Tragkraft</LI>
      </UL>
      <H2>Wie läuft die Futterberatung ab?</H2>
      <OL>
        <LI>
          Analyse der aktuellen Fütterung - Was bekommt dein Pferd derzeit und
          wie sind seine Bedürfnisse?
        </LI>
        <LI>
          Rationsberechnung & Optimierung - Berechnung der Bedarfswerte und
          Zusammenstellung einer individuellen Futterration.
        </LI>
        <LI>
          Individuelle Empfehlungen - Anpassungen an Raufutter, Kraftfutter und
          Ergänzungen, abgestimmt auf den Bedarf deines Pferdes.
        </LI>
      </OL>
      <Paragraph>
        Zusätzlich: Falls gewünscht, können Laborwerte aus einer
        Blutuntersuchung einbezogen werden, um gezielt auf mögliche Mängel oder
        Überschüsse zu reagieren.
      </Paragraph>
      <H2>Ablauf & Kosten</H2>
      <OL>
        <LI>Erstanalyse & individuelle Rationsberechnung</LI>
        <LI>Auswertung & Erstellung eines Futterplans</LI>
        <LI>Besprechung der Ergebnisse & Empfehlungen zur Umsetzung</LI>
      </OL>
      <Paragraph>Dauer: ca. 30-60 Minuten</Paragraph>
      <Paragraph>Kosten: 110 € zzgl. Anfahrt</Paragraph>
      <Paragraph>
        Mit einer individuell angepassten Fütterung kann dein Pferd
        leistungsfähiger, ausgeglichener und gesünder werden. Mein Ziel ist es,
        eine praktische und nachhaltige Lösung zu finden, die optimal zu deinem
        Pferd und seinen Bedürfnissen passt.
      </Paragraph>
    </Leistungsseite>
  );
}

export function BlutabnahmeUndBefundbesprechung() {
  return (
    <Leistungsseite
      title="Blutabnahme und Befundbesprechung"
      description="Blutabnahme und Befundbesprechung für Pferde in Hamburg. Mängel, Stoffwechselstörungen und Entzündungen frühzeitig erkennen und gezielt unterstützen."
      slug="blutabnahme-und-befundbesprechung"
      priceFrom={50}
      backgroundImage={HintergrundBlutabnahme}
    >
      <Headline>
        Gesundheit beginnt im Inneren - Einblicke durch Blutanalysen
      </Headline>
      <Paragraph>
        Die äußere Erscheinung eines Pferdes gibt oft nur begrenzt Aufschluss
        über seinen tatsächlichen Gesundheitszustand. Eine Blutuntersuchung kann
        helfen, Mängel frühzeitig zu erkennen, den Stoffwechsel zu überprüfen
        und gezielte Maßnahmen zur Unterstützung der Gesundheit einzuleiten.
      </Paragraph>
      <Paragraph>
        Ich biete die Blutabnahme sowie eine verständliche Besprechung und
        Interpretation der Werte an, damit du genau weißt, was dein Pferd
        braucht.
      </Paragraph>
      <H2>Warum eine Blutuntersuchung sinnvoll ist</H2>
      <Paragraph>
        Das Blut gibt wertvolle Hinweise auf den Gesundheitszustand deines
        Pferdes. Es hilft, Mangelerscheinungen, Entzündungen oder
        Stoffwechselstörungen frühzeitig zu erkennen, bevor sie sich in
        sichtbaren Symptomen äußern. Eine regelmäßige Kontrolle kann daher
        sowohl zur Diagnostik als auch zur Prävention beitragen.
      </Paragraph>
      <Paragraph>
        Typische Fragestellungen, bei denen eine Blutuntersuchung sinnvoll sein
        kann:
      </Paragraph>
      <UL>
        <LI>Unklare Leistungsabfälle oder Konditionsprobleme</LI>
        <LI>Auffälliges Verhalten oder veränderte Fressgewohnheiten</LI>
        <LI>Mangelerscheinungen (z. B. Eisen, Selen, Zink)</LI>
        <LI>Stoffwechselprobleme (EMS, Cushing, Leber-/Nierenwerte)</LI>
        <LI>Unterstützung bei der Fütterungsanpassung</LI>
        <LI>Verlaufskontrolle nach einer Erkrankung</LI>
      </UL>
      <H2>Was passiert nach der Blutabnahme?</H2>
      <OL>
        <LI>Die Blutprobe wird von einem spezialisierten Labor ausgewertet.</LI>
        <LI>
          Sobald die Ergebnisse vorliegen, erkläre ich dir verständlich, was die
          Werte bedeuten und welche möglichen Maßnahmen sinnvoll wären.
        </LI>
        <LI>
          Falls weiterführende Untersuchungen oder tierärztliche Maßnahmen nötig
          sind, empfehle ich dir die nächsten Schritte.
        </LI>
      </OL>
      <Paragraph>
        Wichtig: Eine Blutuntersuchung kann wertvolle Hinweise liefern, ersetzt
        aber keinen Tierarztbesuch. Falls schwerwiegende Auffälligkeiten
        auftreten, sollte eine tierärztliche Abklärung erfolgen.
      </Paragraph>
      <H2>Ablauf & Kosten</H2>
      <OL>
        <LI>Blutabnahme direkt am Stall</LI>
        <LI>Versand ins Labor & Analyse der Werte</LI>
        <LI>Besprechung der Ergebnisse & individuelle Beratung</LI>
      </OL>
      <Paragraph>Dauer: ca. 20-30 Minuten</Paragraph>
      <Paragraph>Kosten: 50 € zzgl. Laborgebühren & Anfahrt</Paragraph>
      <Paragraph>
        Ob zur Früherkennung, Diagnostik oder Therapieanpassung - eine
        Blutuntersuchung liefert wertvolle Informationen, die helfen, dein Pferd
        optimal zu versorgen.
      </Paragraph>
    </Leistungsseite>
  );
}

export function DryNeedling() {
  return (
    <Leistungsseite
      title="Dry Needling"
      description="Dry Needling für Pferde in Hamburg: gezielte Behandlung hartnäckiger Muskelverspannungen und Triggerpunkte für mehr Beweglichkeit und Losgelassenheit."
      slug="dry-needling"
      priceFrom={80}
      backgroundImage={HintergrundBlutabnahme}
    >
      <Headline>Gezielte Muskel&shy;entspannung für dein Pferd</Headline>
      <Paragraph>
        Muskelverspannungen, verklebte Faszien oder hartnäckige Triggerpunkte
        können die Beweglichkeit deines Pferdes einschränken und langfristig zu
        Rittigkeitsproblemen führen. Oft bleiben diese Spannungen trotz
        klassischer physiotherapeutischer oder osteopathischer Behandlung
        bestehen. Dry Needling ist eine gezielte Technik, um tiefsitzende
        Muskelverspannungen zu lösen und die Beweglichkeit nachhaltig zu
        verbessern.
      </Paragraph>
      <Paragraph>
        Dry Needling arbeitet mit feinen, sterilen Akupunkturnadeln, die direkt
        in verspannte Muskelpartien gesetzt werden. Dadurch wird eine lokale
        Zuckungsreaktion (Twitch-Response) ausgelöst, die den Muskel dazu
        bringt, sich reflektorisch zu entspannen. Gleichzeitig verbessert sich
        die Durchblutung und Sauerstoffversorgung im betroffenen Gewebe, wodurch
        sich die Muskulatur lockert und die Bewegung wieder flüssiger wird.
      </Paragraph>
      <Paragraph>
        Im Gegensatz zur Akupunktur, die auf energetischen Leitbahnen basiert,
        wirkt Dry Needling gezielt im Muskelgewebe. Diese Methode wird
        insbesondere zur Behandlung von hartnäckigen Verspannungen, myofaszialen
        Triggerpunkten und muskulären Dysbalancen eingesetzt.
      </Paragraph>
      <H2>Wie funktioniert Dry Needling?</H2>
      <Paragraph>
        Durch das präzise Setzen der Nadeln in verspannte Muskelbereiche
        entstehen folgende Effekte:
      </Paragraph>
      <UL>
        <LI>
          Lösen von hartnäckigen Muskelverkrampfungen und Entspannung der
          betroffenen Fasern
        </LI>
        <LI>
          Verbesserung der Durchblutung & Sauerstoffversorgung → Schnellere
          Regeneration & Schmerzlinderung
        </LI>
        <LI>
          Förderung der Beweglichkeit → Das Pferd kann sich wieder lockerer und
          ausbalancierter bewegen
        </LI>
      </UL>
      <Paragraph>
        Nach der Behandlung bewegen sich viele Pferde bereits freier, treten
        aktiver unter und zeigen eine verbesserte Losgelassenheit & Anlehnung.
      </Paragraph>
      <H2>Wann ist Dry Needling sinnvoll?</H2>
      <Paragraph>
        Dry Needling kann in vielen Fällen eine wertvolle Unterstützung sein,
        insbesondere bei:
      </Paragraph>
      <UL>
        <LI>Muskulären Verspannungen & Triggerpunkten</LI>
        <LI>Bewegungseinschränkungen, Steifheit oder Rittigkeitsproblemen</LI>
        <LI>
          Nach intensiver Belastung oder in der Reha zur Unterstützung des
          Muskelaufbaus
        </LI>
        <LI>
          Als ergänzende Therapie zu osteopathischen & physiotherapeutischen
          Behandlungen
        </LI>
      </UL>
      <Paragraph>
        Da Dry Needling gezielt in die Muskulatur eingreift, ist es eine ideale
        Ergänzung zur ganzheitlichen Behandlung deines Pferdes. Es hilft dabei,
        muskuläre Blockaden zu lösen, sodass das Pferd anschließend durch
        gezieltes Training wieder in eine gesunde Bewegung geführt werden kann.
      </Paragraph>
      <H2>Ablauf einer Dry Needling-Behandlung</H2>
      <OL>
        <LI>
          Anamnese & Untersuchung - Analyse der Muskulatur, Bewegungsmuster &
          möglicher Spannungen
        </LI>
        <LI>
          Gezieltes Setzen der Nadeln - Behandlung der betroffenen Muskelgruppen
          & Aktivierung der Entspannungsreaktion
        </LI>
        <LI>
          Sanfte Mobilisation & Nachbereitung - Unterstützung der Lockerung
          durch leichte Bewegung
        </LI>
      </OL>
      <Paragraph>
        Nach der Behandlung sollte das Pferd zunächst sanft bewegt werden, um
        die gelöste Muskulatur geschmeidig zu halten und die neue
        Bewegungsfreiheit optimal zu nutzen.
      </Paragraph>
    </Leistungsseite>
  );
}

export function Akupunktur() {
  return (
    <Leistungsseite
      title="Akupunktur"
      description="Akupunktur für Pferde in Hamburg nach TCM: sanfte Unterstützung bei Atemwegserkrankungen, Stoffwechselproblemen, Verspannungen und Stress."
      slug="akupunktur"
      priceFrom={60}
      backgroundImage={Aku2}
    >
      <Headline>Sanfte Impulse für mehr Balance & Gesundheit</Headline>
      <TextMitBildRechts bild={Aku1} alt="Akupunkturbehandlung an einem Pferd">
        <Paragraph>
          Die Akupunktur ist ein jahrtausendealtes Verfahren aus der
          Traditionellen Chinesischen Medizin (TCM) und basiert auf der
          Vorstellung, dass die Lebensenergie – das sogenannte Qi – in einem
          System aus Energieleitbahnen (Meridianen) durch den Körper fließt.
          Kommt es zu Blockaden in diesem Fluss, kann dies zu Störungen im
          Bewegungsapparat, Organfunktion, Immunsystem oder Verhalten führen.
        </Paragraph>
        <Paragraph>
          Durch das gezielte Setzen feiner, steriler Nadeln an bestimmten
          Akupunkturpunkten wird der Energiefluss reguliert, der Stoffwechsel
          angeregt und die Selbstheilungskräfte des Körpers aktiviert. Dabei
          steht nicht nur das Symptom im Vordergrund, sondern immer das Pferd
          als Ganzes.
        </Paragraph>
        <H2>Wann kann Akupunktur sinnvoll sein?</H2>
        <Paragraph>
          Akupunktur kann als eigenständige oder begleitende Therapieform bei
          einer Vielzahl von Beschwerden eingesetzt werden:
        </Paragraph>
        <UL>
          <LI>Chronische Atemwegserkrankungen (z. B. COB, RAO, Asthma)</LI>
          <LI>Verdauungsprobleme & Stoffwechselstörungen</LI>
          <LI>Immunschwäche & Infektanfälligkeit</LI>
          <LI>Muskuläre Verspannungen & Bewegungseinschränkungen</LI>
          <LI>Stress, Unruhe & hormonelle Dysbalancen</LI>
          <LI>
            Rekonvaleszenz & Regeneration nach Erkrankungen oder Verletzungen
          </LI>
        </UL>
      </TextMitBildRechts>
      <TextMitBildLinks bild={Aku3} alt="Gesetzte Akupunkturnadeln am Pferd">
        <H2>Wann ist Akupunktur nicht geeignet?</H2>
        <Paragraph>
          Wie jede Therapie hat auch die Akupunktur ihre Grenzen.
        </Paragraph>
        <Paragraph>
          Nicht oder nur eingeschränkt sollte sie angewendet werden:
        </Paragraph>
        <UL>
          <LI>Bei akuten Infekten mit Fieber</LI>
          <LI>
            Bei hochgradigen Verletzungen, die eine chirurgische oder
            notfallmedizinische Versorgung erfordern
          </LI>
          <LI>
            Bei stark geschwächten oder extrem gestressten Pferden, sollte mit
            besonderer Vorsicht behandelt werden
          </LI>
        </UL>
        <Paragraph>
          In solchen Fällen ist die Zusammenarbeit mit dem Tierarzt essenziell.
        </Paragraph>
        <H2>Wie läuft eine Akupunkturbehandlung ab?</H2>
        <Paragraph>
          Vor der ersten Behandlung erfolgt eine ausführliche Anamnese, bei der
          ich das Pferd in Ruhe betrachte, abtaste und mir ein Bild von seiner
          körperlichen Verfassung mache. Daraufhin wähle ich individuell
          passende Akupunkturpunkte, die ich mit dünnsten sterilen Nadeln
          stimuliere.
        </Paragraph>
        <Paragraph>
          Eine Sitzung dauert in der Regel 30 bis 60 Minuten – je nach Pferd und
          Thema. Viele Pferde zeigen während der Behandlung sichtbare
          Entspannung.
        </Paragraph>
      </TextMitBildLinks>
      <TextMitBildRechts bild={Aku4} alt="Pferd während einer Akupunktursitzung">
        <H2>Wirkung und Ziel der Behandlung</H2>
        <Paragraph>Akupunktur wirkt auf verschiedenen Ebenen:</Paragraph>
        <UL>
          <LI>Aktivierung der Selbstheilungskräfte</LI>
          <LI>Verbesserung von Durchblutung & Lymphfluss</LI>
          <LI>Entspannung der Muskulatur & Reduktion von Schmerzen</LI>
          <LI>Stärkung von Immunsystem & Organfunktion</LI>
          <LI>Harmonisierung des vegetativen Nervensystems</LI>
        </UL>
        <Paragraph>
          Die Behandlung ist sanft, gut verträglich und kann auch mit anderen
          Methoden wie Osteopathie, Physiotherapie oder Dry Needling kombiniert
          werden.
        </Paragraph>
        <H2>Für ganzheitliche Balance & Vitalität</H2>
        <Paragraph>
          Akupunktur ist besonders geeignet für Pferde mit chronischen
          Beschwerden, Dysbalancen oder erhöhtem Stresslevel. Sie unterstützt
          das Pferd dabei, wieder in ein gesundes Gleichgewicht zu finden – von
          innen heraus.
        </Paragraph>
      </TextMitBildRechts>
    </Leistungsseite>
  );
}

export function Hofmag() {
  return (
    <Leistungsseite
      title="Hofmag Therapie"
      description="Hofmag PEMF Magnetfeldtherapie für Pferd und Hund in Hamburg. Schnupper-, Wellness-, Therapie- und Verletzungsbehandlung ab 30 €."
      slug="hofmag-therapie"
      priceFrom={30}
      backgroundImage={HintergrundHofmag}
    >
      <Headline>
        Hochmoderne Magnetfeld&shy;therapie für dein Pferd und Hund
      </Headline>
      <TextMitBildRechts bild={Hofmag1} alt="Hofmag Magnetfeldtherapie-Anwendung am Pferd">
        <H2>Was ist Hofmag?</H2>
        <Paragraph>
          Hofmag ist eine moderne, gepulste elektromagnetische Feldtherapie
          (PEMF), bei der kurze, hochintensive Magnetimpulse eingesetzt werden.
          Diese durchdringen tief das Gewebe - Multiplikator-Wirkung bis zu
          20&nbsp;cm - und stimulieren körpereigene Selbstheilungsprozesse auf
          zellulärer Ebene.
        </Paragraph>
        <H2>Hauptvorteile von Hofmag</H2>
        <OL>
          <LI>
            Aktivierung der Selbstheilung: Hofmag-Impulse erhöhen nachweislich
            die ATP-Produktion in den Zellen, aktivieren den Stoffwechsel und
            fördern natürliche Regeneration.
          </LI>
          <LI>
            Schmerzreduktion & Entzündungshemmung: PEMF wirkt schmerzlindernd,
            reduziert Entzündungen und unterstützt die Gewebereparatur.
          </LI>
          <LI>
            Muskelentspannung & Faszienstimulation: Regeneriert verspannte
            Muskeln, löst Faszienverklebungen und reduziert Krämpfe.
          </LI>
          <LI>
            Steigerung der Beweglichkeit: Erhöht den Bewegungsumfang durch Lösen
            von Muskel- und Faszienverkürzungen.
          </LI>
          <LI>
            Leistungsförderung & Prävention: Fördert Durchblutung,
            Sauerstoffversorgung und Nährstoffzufuhr - ideal zur
            Leistungssteigerung und Verletzungsprävention.
          </LI>
          <LI>
            Regeneration & Rehabilitation: Unterstützt Heilung bei Knochen-,
            Sehnen-, Bindegewebe- und Hautverletzungen, sowie bei Hufrehe
            (Laminitis).
          </LI>
        </OL>
        <H2>Das Hofmag Gerät: Modell K8</H2>
        <UL>
          <LI>
            Eines der leistungsstärksten PEMF-Systeme (bis 650&nbsp;Gauss,
            6&nbsp;Hz Pulsfrequenz)
          </LI>
          <LI>Tiefe Gewebedurchdringung bis 20&nbsp;cm</LI>
          <LI>CE&#8209;zertifiziert und FEI&#8209;zugelassen</LI>
        </UL>
      </TextMitBildRechts>
      <TextMitBildLinks bild={Hofmag2} alt="Hofmag während einer Behandlung am Pferd">
        <H2>Wissenschaft & Studien</H2>
        <Paragraph>
          Hofmag wurde in einer randomisierten Doppelblindstudie (RCT)
          untersucht - dem wissenschaftlichen Goldstandard. Die Ergebnisse
          zeigen signifikante Effekte in den Bereichen Muskeltonus,
          Gelenkbeweglichkeit und Schmerzreduktion bei Pferden.
        </Paragraph>
        <H2>Erfahrungen von Profis</H2>
        <UL>
          <LI>Jessica von Bredow Werndl, Dressurreiterin</LI>
          <LI>Simone Blum, Springreiterin</LI>
          <LI>Jack Whitaker, Springreiter</LI>
        </UL>
        <Paragraph>
          Zusätzlich bestätigen weitere Equine-Therapeuten und Reha-Zentren die
          Erfolge bei Schmerzfreiheit, Muskelregeneration und verbesserter
          Beweglichkeit.
        </Paragraph>
        <H2>Anwendungsbereiche</H2>
        <Paragraph>
          Pferde & Kleintiere: Muskel- & Gelenkprobleme, Sehnen- &
          Bänderschäden, Arthrose, Hufrehe, Wund- & Knochenheilung, Prävention
          bei Turnieren
        </Paragraph>
        <Paragraph>
          Menschen: Schmerzen im Bewegungsapparat, Faszienprobleme,
          post-operative Regeneration, Haut&shy;erkrankungen, Migräne u.v.m.
          (seit Januar 2021 zugelassen)
        </Paragraph>
      </TextMitBildLinks>
      <TextMitBildRechts bild={Hofmag3} alt="Hofmag Behandlungssitzung an einem Pferd">
        <H2>Schnupperbehandlung 40€</H2>
        <UL>
          <LI>Kennenlernen des Hofmag-Systems</LI>
          <LI>Sanfte, kurze Anwendung</LI>
          <LI>Ideal für empfindliche oder ängstliche Tiere</LI>
          <LI>Unverbindlich & zum Ausprobieren</LI>
          <LI>Inklusive Vorgespräch</LI>
          <LI>ca. 20 Minuten Gesamtdauer</LI>
          <LI>Einmalig buchbar pro Kunde</LI>
        </UL>
        <H2>Wellness-Behandlung 60€</H2>
        <UL>
          <LI>Entspannende Anwendung zur Lockerung der Muskulatur</LI>
          <LI>Unterstützt Regeneration & Wohlbefinden</LI>
          <LI>Ideal nach dem Training oder als regelmäßige Pflege</LI>
          <LI>ca. 30 Minuten Gesamtdauer</LI>
        </UL>
        <H2>Therapie-Behandlung 80€</H2>
        <UL>
          <LI>Zielgerichtete Anwendung bei konkreten Beschwerden</LI>
          <LI>
            Unterstützt den Heilungsverlauf (z.B. bei Arthrose, Verspannungen,
            Stoffwechselproblemen)
          </LI>
          <LI>Kombination mit osteopathischer Begleitbehandlung möglich</LI>
          <LI>ca. 45 Minuten Gesamtdauer</LI>
        </UL>
        <H2>Behandlung bei Verletzungen 30€</H2>
        <UL>
          <LI>
            Förderung der Heilung bei Sehnen-, Bänder- oder Muskelverletzungen
          </LI>
          <LI>Entzündungshemmend & durchblutungsfördernd</LI>
          <LI>Auch zur Narben- und Gewebeheilung nach OPs einsetzbar</LI>
          <LI>Nach Möglichkeit nach Absprache mit dem Tierarzt</LI>
          <LI>15 Minuten Hofmag Anwendung</LI>
        </UL>
      </TextMitBildRechts>
    </Leistungsseite>
  );
}
